import { Loading } from "../../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  NOTIFY_GROUPS,
  NOTIFY_LABELS,
  type BuildingDto,
  type NotificationPrefs,
  type NotificationPrefsResponse,
} from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";

/** Eigen e-mailmeldingen voor dit gebouw (Gebouw → Notificaties). */
export function NotificationsPanel({ building: b }: { building: Pick<BuildingDto, "id" | "code" | "name"> }) {
  const toast = useToast();
  const qc = useQueryClient();
  const key = ["notifications", b.id];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<NotificationPrefsResponse>(`/buildings/${b.id}/notifications`),
  });
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  useEffect(() => {
    if (q.data && !prefs) setPrefs(q.data.prefs);
  }, [q.data, prefs]);

  const save = useMutation({
    mutationFn: (p: NotificationPrefs) =>
      api<NotificationPrefsResponse>(`/buildings/${b.id}/notifications`, { method: "PUT", body: p }),
    onSuccess: (res) => {
      qc.setQueryData(key, res);
      setPrefs(res.prefs);
      toast("Notificaties opgeslagen");
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : "Opslaan mislukt"),
  });

  const mail = q.data?.mailEnabled ?? true;
  const disabled = !prefs || save.isPending;
  const changed = Boolean(prefs && q.data && JSON.stringify(prefs) !== JSON.stringify(q.data.prefs));

  return (
    <section className="panel" aria-label="Notificaties" aria-busy={save.isPending}>
      <p className="muted">
        Kies welke e-mails je over {b.name} wilt krijgen. Notificaties worden een paar minuten verzameld en in één mail
        gestuurd; spoed gaat meteen.
      </p>
      {q.isError && (
        <div className="alert" role="alert">
          Notificaties konden niet worden geladen.
        </div>
      )}
      {!mail && (
        <div className="note" role="status">
          E-mail staat nog niet aan op de server. Je keuze geldt zodra dat wel zo is.
        </div>
      )}
      {q.isPending && <Loading />}
      {prefs &&
        NOTIFY_GROUPS.map((g) => (
          <fieldset key={g.l} className="checklist">
            <legend className="label">{g.l}</legend>
            {g.kinds.map((k) => (
              <label key={k}>
                <input
                  type="checkbox"
                  checked={prefs[k]}
                  onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })}
                />
                <span>
                  {NOTIFY_LABELS[k].l}
                  <br />
                  <span className="muted small">{NOTIFY_LABELS[k].t}</span>
                </span>
              </label>
            ))}
          </fieldset>
        ))}
      <div className="btnrow">
        <button
          type="button"
          className="btn primary"
          disabled={disabled || !changed}
          onClick={() => prefs && save.mutate(prefs)}
        >
          Opslaan
        </button>
      </div>
    </section>
  );
}
